import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { useKeyboardHighlightProvider } from '@atlassian/search-dialog/search-keyboard';

import { messages } from './messages.tsx';

const styles = cssMap({
	hiddenTitle: {
		position: 'absolute',
		width: '0px',
		height: '0px',
		padding: '0',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		border: 'none',
	},
});

/**
 * SearchDialogAnnouncer
 *
 * An aria-live region that announces the state of the Quick Find dialog to
 * screen readers - the number of available items, the no-results message when
 * a search returns nothing, or, when the search fails, the error message.
 *
 * Announcing through a single, always-mounted live region (by clearing and
 * re-setting its text) is more reliable than relying on a `role="alert"` node
 * that is inserted together with the dialog body: assistive technology only
 * consistently announces changes that happen *within* a live region that was
 * already present.
 *
 * @param isLoading - When true, suppresses count announcements while results load.
 * @param isError - When true, announces the search error message assertively.
 * @param isNoResults - When true, announces the no-results empty state politely.
 */
export const SearchDialogAnnouncer = ({
	isLoading,
	isError = false,
	isNoResults = false,
}: {
	isLoading: boolean;
	isError?: boolean;
	isNoResults?: boolean;
}): React.JSX.Element => {
	const dialogAnnouncerRef = useRef<HTMLDivElement>(null);
	const keyboardNavComponents = useKeyboardHighlightProvider()?.keyboardNavComponents;
	const { formatMessage } = useIntl();

	useEffect(() => {
		if (!dialogAnnouncerRef.current) {
			return;
		}

		// Errors take priority and are announced even while loading.
		if (isError) {
			// Clear then re-set next frame so screen readers treat it as a new announcement.
			dialogAnnouncerRef.current.textContent = '';
			const rafId = requestAnimationFrame(() => {
				if (dialogAnnouncerRef.current) {
					dialogAnnouncerRef.current.textContent = formatMessage(messages.errorAnnouncement);
				}
			});
			return () => cancelAnimationFrame(rafId);
		}

		if (!isLoading) {
			// Clear then re-set next frame so screen readers treat it as a new announcement.
			dialogAnnouncerRef.current.textContent = '';
			const rafId = requestAnimationFrame(() => {
				if (!dialogAnnouncerRef.current) {
					return;
				}
				// Announce the no-results empty state (AI-1315); otherwise announce the count.
				if (isNoResults) {
					dialogAnnouncerRef.current.textContent = formatMessage(messages.noResultsAnnouncement);
				} else if (keyboardNavComponents?.length) {
					dialogAnnouncerRef.current.textContent = formatMessage(messages.availableOptions, {
						count: keyboardNavComponents.length,
					});
				}
			});
			return () => cancelAnimationFrame(rafId);
		}

		// Loading with no error: clear any stale text (e.g. a previous error) so it
		// is not left in the live region during the loading window.
		dialogAnnouncerRef.current.textContent = '';
	}, [keyboardNavComponents?.length, isLoading, isError, isNoResults, formatMessage]);

	return (
		<Box
			xcss={styles.hiddenTitle}
			aria-live={isError ? 'assertive' : 'polite'}
			aria-atomic={true}
			ref={dialogAnnouncerRef}
		/>
	);
};
